import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useDiagnosis, useDiagnosisCustomerSites, useDiagnosisCustomer } from '../hooks';
import { PageLayout } from '../components/layout';
import { ActionButton } from '../components/common';
import { CreateDiagnosisDialog } from '../components/diagnosis/CreateDiagnosisDialog';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { useDeleteDiagnosis } from '../hooks/useDiagnosis';
import { useQuery } from 'react-query';
import { apiClient } from '../services/apiClient';
import { UserReadDTO } from '../generated/api-client';
import { dateLikeToIsoString } from '../utils/date.utils';
import { DiagnosisTable } from '../components/diagnosis/DiagnosisTable';
import { isDiagnosisClosed } from '../utils/diagnosisStatus';

export const DiagnosisPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: diagnosisList = [], isLoading, error } = useDiagnosis();
  const { data: diagnosisSites = [] } = useDiagnosisCustomerSites();
  const { data: diagnosisCustomers = [] } = useDiagnosisCustomer();
  const { data: users = [] } = useQuery<UserReadDTO[]>('users', () =>
    apiClient.list_items_user__get()
  );
  const [isDialogOpen, setDialogOpen] = React.useState(false);
  const deleteMutation = useDeleteDiagnosis();
  const [deleteState, setDeleteState] = React.useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });

  const siteMap = React.useMemo(() => {
    const map = new Map<string, (typeof diagnosisSites)[number]>();
    diagnosisSites.forEach(site => {
      if (site.id) {
        map.set(site.id, site);
      }
    });
    return map;
  }, [diagnosisSites]);

  const customerMap = React.useMemo(() => {
    const map = new Map<string, (typeof diagnosisCustomers)[number]>();
    diagnosisCustomers.forEach(customer => {
      if (customer.id) {
        map.set(customer.id, customer);
      }
    });
    return map;
  }, [diagnosisCustomers]);

  const userMap = React.useMemo(() => {
    const map = new Map<string, UserReadDTO>();
    users.forEach(user => {
      if (user.id) {
        map.set(user.id, user);
      }
    });
    return map;
  }, [users]);

  const rows = React.useMemo(() => {
    return diagnosisList.map(diagnosis => {
      const site = diagnosis.site_id ? siteMap.get(diagnosis.site_id) : undefined;
      const customer = site?.customer_id ? customerMap.get(site.customer_id) : undefined;
      const responsible = diagnosis.responsible_01
        ? userMap.get(diagnosis.responsible_01)
        : undefined;
      const responsibleName = responsible
        ? `${responsible.firstname} ${responsible.lastname}`.trim()
        : '—';
      const customerLabel =
        [customer?.company_name, (customer as { company_name_type?: string })?.company_name_type]
          .filter(Boolean)
          .join(' ') || '—';
      const did = diagnosis.id ?? '';
      const isClosed = isDiagnosisClosed(diagnosis);
      const progress = isClosed ? 101 : (diagnosis.checklist_progress_percent ?? 0);
      return {
        id: did,
        table_code_index: diagnosis.table_code_index,
        customer_name: customerLabel,
        site_address: site?.address ?? '—',
        responsible_01: responsibleName,
        last_update: dateLikeToIsoString(diagnosis.last_update),
        progress,
        _progressLoading: false,
      };
    });
  }, [diagnosisList, siteMap, customerMap, userMap]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleCreatedDiagnosis = (diagnosisId: string) => {
    setDialogOpen(false);
    navigate(`/diagnosis/${diagnosisId}`, { replace: true });
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Errore nel caricamento delle diagnosi.</Typography>
      </Box>
    );
  }

  return (
    <>
      <PageLayout
        header={
          <>
            <AssignmentIcon fontSize="large" />
            <Typography variant="h5" sx={{ marginLeft: '1rem' }}>
              Diagnosi energetiche
            </Typography>
          </>
        }
        actions={
          <ActionButton icon={<AddIcon />} tooltip="Nuova diagnosi" onClick={handleOpenDialog} />
        }
      >
        <Box sx={{ p: 2 }}>
          <DiagnosisTable
            rows={rows as any}
            onRequestDelete={id => setDeleteState({ open: true, id })}
            loading={isLoading}
            isFiltrable
          />
        </Box>
      </PageLayout>
      <CreateDiagnosisDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onCreated={handleCreatedDiagnosis}
      />
      <ConfirmDialog
        open={deleteState.open}
        title="Conferma eliminazione"
        content="Sei sicuro di voler eliminare questa diagnosi?"
        confirmText="Elimina"
        cancelText="Annulla"
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteState({ open: false, id: null })}
        onConfirm={() => {
          if (deleteState.id) {
            deleteMutation.mutate(deleteState.id, {
              onSettled: () => setDeleteState({ open: false, id: null }),
            });
          }
        }}
      />
    </>
  );
};
